import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GradeBarChartKey } from "./GradeBarChartKey";

import "./GradeComparison.css";
import { GradeScatterChartKey } from "./GradeScatterChartKey";
import { GradeBarChart } from "./GradeBarChart";
import { GradeScatterChart } from "./GradeScatterChart";
import { GradeImageChart } from "./GradeImageChart";

type Props = {
    data?: number[][];
    imageUrls?: string[];
    type: string;
    ageRange: string;
    vesselGrade?: number;
    vesselGradeRange?: string;
    vesselAge: number;
};

export const GradeComparison = (props: Props) => {
    const {
        data,
        imageUrls,
        type,
        ageRange,
        vesselGrade,
        vesselGradeRange,
        vesselAge
    } = props;

    const { t } = useTranslation("common");

    const renderBarChart = useCallback(() => {
        if (data) {
            return (
                <GradeBarChart
                    data={data}
                    vesselGrade={vesselGrade}
                    vesselGradeRange={vesselGradeRange}
                />
            );
        }

        if (imageUrls && imageUrls[0]) {
            return <GradeImageChart imageUrl={imageUrls[0]} />;
        }

        return null;
    }, [data, imageUrls]);

    const renderScatterChart = useCallback(() => {
        if (data) {
            return (
                <GradeScatterChart
                    data={data}
                    ageRange={ageRange}
                    vesselAge={vesselAge}
                    vesselGrade={vesselGrade}
                    vesselGradeRange={vesselGradeRange}
                />
            );
        }

        if (imageUrls && imageUrls[1]) {
            return <GradeImageChart imageUrl={imageUrls[1]} />;
        }

        return null;
    }, [data, imageUrls]);

    return (
        <div>
            <p className="grade-info">{`${t(
                "inspectionReport.gradeComparison.info"
            )}`}</p>
            <h2 className="grade-header">{`${t(
                "inspectionReport.gradeComparison.barChartTitle",
                {
                    type
                }
            )}`}</h2>
            <div className="grid" id="comparisonGraphContainer">
                {renderBarChart()}
                <GradeBarChartKey estimated={!!vesselGradeRange} />
            </div>
            <h2 className="grade-header mt-2">{`${t(
                "inspectionReport.gradeComparison.scatterChartTitle",
                {
                    type,
                    ageRange
                }
            )}`}</h2>
            <div className="grid" id="scatterGraphContainer">
                {renderScatterChart()}
                <GradeScatterChartKey estimated={!!vesselGradeRange} />
            </div>
            <p className="grade-info mt-0">{`${t(
                "inspectionReport.gradeComparison.footnote"
            )}`}</p>
        </div>
    );
};
